import moment from 'moment';

// Short, human-readable label for a bracket match based on its round name and
// 1-indexed position within that round. Returns "QF-2", "SF-1", "Final",
// "R16-3", or "Match N" as fallback.
export const getBracketMatchLabel = (roundName, positionInRound) => {
  const rn = (roundName || '').toLowerCase();
  if (rn.includes('quarter')) return `QF-${positionInRound}`;
  if (rn.includes('semi')) return `SF-${positionInRound}`;
  if (rn === 'final') return 'Final';
  if (rn.includes('round of')) return `R${rn.replace(/\D/g, '')}-${positionInRound}`;
  return `Match ${positionInRound}`;
};

export const calculateMatchDuration = (phase) => {
  if (!phase?.matchRules) return 60;

  const { gameDuration, breaks, breakDuration } = phase.matchRules;
  return gameDuration * breaks + breakDuration * (breaks - 1);
};

// matchTime is now a UTC ISO instant. Add minutes and return the resulting
// wall-clock HH:mm in the viewer's local TZ.
export const calculateMatchEndTime = (matchTime, duration) => {
  if (!matchTime) return '';
  return moment(matchTime).add(duration, 'minutes').format('HH:mm');
};

// Formats a UTC ISO instant for display in the viewer's local timezone.
export const formatMatchDate = (date, format = 'dddd, MMMM D, YYYY') => {
  return moment(date).format(format);
};

export const isFixtureCompleted = (fixture) => {
  return fixture.status === 'COMPLETED' && fixture.hasResult && fixture.eventId;
};

export const canAddResult = (fixture) => {
  return fixture.homeTeamActualId && fixture.awayTeamActualId && fixture.eventId;
};

export const getFixtureScore = (fixture) => {
  if (!isFixtureCompleted(fixture) || !fixture.eventId) {
    return { home: 0, away: 0 };
  }

  return {
    home: fixture.eventId?.homeTeam?.scores || 0,
    away: fixture.eventId?.awayTeam?.scores || 0,
  };
};

// Groups fixtures by viewer-local calendar day derived from matchTime (UTC ISO).
export const groupFixturesByDate = (fixtures) => {
  return fixtures.reduce((acc, fixture) => {
    if (!fixture.matchTime) return acc;
    const dayKey = moment(fixture.matchTime).format('YYYY-MM-DD');
    if (!acc[dayKey]) {
      acc[dayKey] = [];
    }
    acc[dayKey].push(fixture);
    return acc;
  }, {});
};

// Sorts fixtures by their UTC instant.
export const sortFixturesByDateTime = (fixtures) => {
  return fixtures.sort((a, b) => {
    const aMs = a?.matchTime ? new Date(a.matchTime).valueOf() : 0;
    const bMs = b?.matchTime ? new Date(b.matchTime).valueOf() : 0;
    return aMs - bMs;
  });
};

export const getPitchColor = (pitchName) => {
  const colors = [
    'linear-gradient(135deg, #1a1a1a 0%, #2196F3 100%)',
    'linear-gradient(135deg, #1a1a1a 0%, #1976D2 100%)',
    'linear-gradient(135deg, #1a1a1a 0%, #0D47A1 100%)',
    'linear-gradient(135deg, #1a1a1a 0%, #1565C0 100%)',
    'linear-gradient(135deg, #1a1a1a 0%, #1E88E5 100%)',
  ];

  const index = pitchName.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;
  return colors[index];
};

export const generateCSVData = (fixtures, selectedCategory, findTeamInSeason) => {
  const headers = [
    'Starting time',
    'End time',
    'Day',
    'Playing field',
    'Phase',
    'Division',
    'Group',
    'Team 1',
    'Team 2',
  ];

  const rows = fixtures.map((fixture) => {
    const homeTeam = findTeamInSeason(fixture.homeTeamId);
    const awayTeam = findTeamInSeason(fixture.awayTeamId);

    const phase = selectedCategory?.phases?.find((p) => p.name === fixture.phaseName);
    const matchDuration = calculateMatchDuration(phase);
    const startMoment = fixture.matchTime ? moment(fixture.matchTime) : null;
    const startTime = startMoment ? startMoment.format('HH:mm') : '';
    const endTime = calculateMatchEndTime(fixture.matchTime, matchDuration);

    return [
      startTime,
      endTime,
      startMoment ? startMoment.format('DD-MM-YYYY') : '',
      fixture.pitch,
      fixture.phaseName,
      selectedCategory?.name || '',
      fixture.groupName,
      homeTeam?.teamId?.title || 'TBD',
      awayTeam?.teamId?.title || 'TBD',
    ];
  });

  return { headers, rows };
};

export const downloadCSV = (headers, rows, filename = 'fixtures.csv') => {
  const csvContent = [
    headers.join(','),
    ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
